/**
 * Sign-in, in the prototype's look: the looping video at half opacity over black, the logo as wide
 * as the panel, the strapline beneath it, and one small panel to sign in with - an emailed link by
 * default, a password for those who set one (decision 3.5a), and Google once it is enabled.
 */

import { KeyRound, Mail } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router";

import { useAuth } from "@/auth/AuthProvider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding } from "@/lib/useBranding";

type Sent = { to: string; kind: "sign-in" | "password" };

export function LoginPage() {
  const { state, sendSignInLink, sendPasswordLink, signInWithPassword, signInWithGoogle, signInForDevelopment } =
    useAuth();
  const { brand } = useBranding();
  const [mode, setMode] = useState<"link" | "password">("link");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState<Sent | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.status === "signed-in") return <Navigate to="/" replace />;
  const methods = state.status === "signed-out" ? state.methods : [];
  const dev = methods.includes("dev");
  const passwords = methods.includes("password") && !dev;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const address = email.trim();
    if (dev) void run(() => signInForDevelopment(address));
    else if (mode === "password") void run(() => signInWithPassword(address, password));
    else
      void run(async () => {
        await sendSignInLink(address);
        setSent({ to: address, kind: "sign-in" });
      });
  }

  function onPasswordLink() {
    const address = email.trim();
    if (!address) {
      setError("enter your email first");
      return;
    }
    void run(async () => {
      await sendPasswordLink(address);
      setSent({ to: address, kind: "password" });
    });
  }

  function switchMode(next: "link" | "password") {
    setMode(next);
    setError(null);
    setPassword("");
  }

  return (
    <div className="relative min-h-dvh overflow-hidden bg-black">
      <video
        className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-50"
        src={brand.login_video_url}
        poster={brand.login_poster_url}
        autoPlay
        loop
        muted
        playsInline
        aria-hidden="true"
      />
      <main className="relative mx-auto flex min-h-dvh w-full max-w-[380px] flex-col justify-center px-4 py-10">
        <img src={brand.logo_url} alt={brand.product_name} className="mx-auto mb-4 block h-auto w-full max-w-[300px]" />
        {brand.tagline && <Tagline text={brand.tagline} />}

        <div className="rounded-xl border border-white/12 bg-[rgb(8_11_15/0.72)] p-5 backdrop-blur-sm">
          {state.status === "loading" && <p className="text-sm text-muted-foreground">Loading…</p>}
          {state.status === "unavailable" && (
            <Alert variant="destructive">
              <AlertDescription>Sign-in is unavailable right now: {state.reason}</AlertDescription>
            </Alert>
          )}

          {sent ? (
            <div className="space-y-2 text-sm">
              <p className="font-medium">Check your inbox</p>
              <p className="text-muted-foreground">
                {sent.kind === "sign-in" ? (
                  <>
                    We sent a sign-in link to <span className="text-foreground">{sent.to}</span>. Open it on this
                    device to continue.
                  </>
                ) : (
                  <>
                    If <span className="text-foreground">{sent.to}</span> has an account, we sent it a link to choose
                    a password.
                  </>
                )}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setSent(null)}>
                Back
              </Button>
            </div>
          ) : (
            (methods.includes("email_link") || dev) && (
              <form onSubmit={onSubmit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="email">{dev ? "Development sign-in" : "Email"}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                  />
                </div>
                {mode === "password" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={busy}>
                  {mode === "password" ? <KeyRound aria-hidden="true" /> : <Mail aria-hidden="true" />}
                  {dev ? "Sign in (local only)" : mode === "password" ? "Sign in" : "Email me a sign-in link"}
                </Button>
                {passwords && (
                  <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs">
                    {mode === "link" ? (
                      <TextButton onClick={() => switchMode("password")}>Use a password instead</TextButton>
                    ) : (
                      <>
                        <TextButton onClick={onPasswordLink} disabled={busy}>
                          Forgot or never set a password?
                        </TextButton>
                        <TextButton onClick={() => switchMode("link")}>Email me a link instead</TextButton>
                      </>
                    )}
                  </div>
                )}
              </form>
            )
          )}

          {methods.includes("google") && !sent && (
            <>
              <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-white/10" />
                or
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <Button variant="outline" className="w-full" disabled={busy} onClick={() => void run(signInWithGoogle)}>
                Continue with Google
              </Button>
            </>
          )}

          {error && (
            <Alert variant="destructive" className="mt-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">
          By continuing you agree to the{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-white">Terms of Service</Link> and{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-white">Privacy Policy</Link>.
        </p>
      </main>
    </div>
  );
}

function TextButton({ children, onClick, disabled }: { children: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** Breaks the tagline after its first comma and sets the rest in italics. */
function Tagline({ text }: { text: string }) {
  const comma = text.indexOf(",");
  const lead = comma < 0 ? text : text.slice(0, comma + 1);
  const tail = comma < 0 ? "" : text.slice(comma + 1).trim();
  return (
    <p className="mb-6 text-center text-2xl text-slate-200">
      {lead}
      {tail && (
        <>
          <br />
          <em>{tail}</em>
        </>
      )}
    </p>
  );
}
